import { useState } from 'react';
import * as React from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';

import type { RefundReason } from '@klangio/ai2notes-ts-client';
import { TranscriptionService } from '@klangio/ai2notes-ts-client';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import type { WithUser } from '../../../context';
import { withUserContext } from '../../../context';

import type { Transcription } from '../../../entities';
import { TrackingAction, TrackingCategory, trackingService } from '../../../services';

const useStyles = makeStyles()(() => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1em',
    maxWidth: '30em',
  },
  gap: {
    margin: '1em 0',
  },
}));

export interface RefundDialogProps extends WithUser {
  open: boolean;
  onClose: () => void;
  transcription: Transcription;
  updateTranscriptions: () => void;
}

export enum Reasons {
  'OTHER' = 0,
  'TOO_MANY_ERRORS' = 1,
  'QUALITY_ISSUES' = 2,
  'UPGRADED_BY_ACCIDENT' = 3,
  'BROKEN_EXPORT' = 4,
  'INTERNAL_PROCESS_ERROR' = 5,
}

const RefundDialog: React.FC<RefundDialogProps> = ({
  refresh,
  user,
  open,
  onClose,
  transcription,
  updateTranscriptions,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [selectedOption, setSelectedOption] = useState<Reasons>(null);
  const [freeTextValue, setFreeTextValue] = useState('');
  const [fieldError, setFieldError] = useState(false);
  const [freeTextError, setFreeTextError] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (event: any) => {
    setSelectedOption(event.target.value);
    setFieldError(false);
  };

  const handleFreeTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFreeTextError(false);
    setFreeTextValue(event.target.value);
  };

  const onSubmit = async () => {
    if (selectedOption === null) {
      setFieldError(true);
      return;
    }
    if (selectedOption === Reasons.OTHER && !freeTextValue) {
      setFreeTextError(true);
      return;
    }
    setPolling(true);
    trackingService.event(TrackingCategory.Buy, TrackingAction.BUY_REFUND);

    const refundInfo = {
      message: freeTextValue,
      reason: selectedOption as unknown as RefundReason,
    };
    TranscriptionService.transcriptionRefund(transcription.id, refundInfo)
      .then(() => {
        setPolling(false);
        setSuccess(true);
      })
      .catch(() => {
        setPolling(false);
        setError(true);
      })
      .finally(() => {
        setTimeout(() => {
          updateTranscriptions();
          if (user) refresh();
        }, 500);
      });
  };

  if (success) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogContent className={classes.content}>
          <Typography align="center" variant="h5">
            <strong>{t('dialog.refund.title')}</strong>
          </Typography>
          <div className={classes.gap}>
            <Alert>{t('dialog.refund.succeed')}</Alert>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined" color="primary">
            {t('dialog.refund.close')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent className={classes.content}>
        <Typography variant="h5" align="center">
          <strong>{t('dialog.refund.title')}</strong>
        </Typography>
        <Typography variant="body1" align="center">
          {t('dialog.refund.subtitle')}
        </Typography>
        <FormControl fullWidth required>
          <InputLabel error={fieldError} htmlFor="reason" id="reason-label">
            {t('dialog.refund.reason_label')}
          </InputLabel>
          <Select
            fullWidth
            name="reason"
            id="reason"
            defaultValue=""
            value={selectedOption?.toString()}
            onChange={handleChange}
          >
            <MenuItem value={Reasons.TOO_MANY_ERRORS}>
              {t('dialog.refund.reasons.technical_issues')}
            </MenuItem>
            <MenuItem value={Reasons.QUALITY_ISSUES}>
              {t('dialog.refund.reasons.quality_issues')}
            </MenuItem>
            <MenuItem value={Reasons.UPGRADED_BY_ACCIDENT}>
              {t('dialog.refund.reasons.upgraded_by_accident')}
            </MenuItem>
            <MenuItem value={Reasons.BROKEN_EXPORT}>
              {t('dialog.refund.reasons.broken_export')}
            </MenuItem>
            <MenuItem value={Reasons.OTHER}>{t('dialog.refund.reasons.other')}</MenuItem>
          </Select>
          {fieldError && <FormHelperText error>{t('common.required')}</FormHelperText>}
        </FormControl>

        {selectedOption === 0 ? (
          <FormControl fullWidth required>
            <TextField
              id="otherReason"
              onChange={handleFreeTextChange}
              label={t('dialog.cancel_subscription.free_text_reason')}
              value={freeTextValue}
              inputProps={{ maxLength: 300 }}
              fullWidth
              type="text"
            />
            {freeTextError && <FormHelperText error>{t('common.required')}</FormHelperText>}
          </FormControl>
        ) : (
          <TextField
            fullWidth
            id="freetext"
            onChange={handleFreeTextChange}
            multiline
            inputProps={{ maxLength: 300 }}
            label={t('dialog.refund.free_text_label')}
            value={freeTextValue}
            type="text"
            helperText={t('dialog.refund.free_text_helper')}
          />
        )}
        {error && <Alert severity="error">{t('common.error')}</Alert>}

        {polling ? (
          <div className={classes.gap}>
            <CircularProgress size={60} />
            <br />
          </div>
        ) : (
          <DialogActions>
            <Button variant="contained" color="primary" onClick={onSubmit}>
              {t('dialog.refund.submit')}
            </Button>
            <Button onClick={onClose} variant="outlined" color="primary">
              {t('dialog.refund.cancel')}
            </Button>
          </DialogActions>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default withUserContext(RefundDialog);
